import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, Database, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { InsightsApi, insightKeys } from "@/api/perkpoint/insights";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";
import { useActiveProgramId } from "../programs/program-scope";
import {
  TestDataApi,
  testDataKeys,
  type TestDataOptions,
  type TestDataRun,
} from "@/api/perkpoint/testData";

const OPEN_EVENT = "perkpoint:open-settings";

/** Open the Perk Point settings modal from anywhere (e.g. the sidebar). */
export function openPerkPointSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  hint,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value);
          onChange(Number.isFinite(next) ? Math.min(max, Math.max(min, Math.round(next))) : min);
        }}
        className="h-10 bg-white"
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Notice({ tone, children }: { tone: "warn" | "info"; children: ReactNode }) {
  return (
    <p
      className={
        tone === "warn"
          ? "flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          : "rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
      }
    >
      {tone === "warn" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : null}
      <span>{children}</span>
    </p>
  );
}

const SETUP_ITEMS = [
  ["outlets", "Outlets", "Outlet 1, Outlet 2, Online, and Airport kiosk"],
  ["rules", "Rules", "Live earn, redemption and validity rules, plus one pending approval and one draft"],
  ["fraud", "Fraud rules and alerts", "Flag-only rules, with a few same-day bursts so alerts are raised"],
  ["campaigns", "Campaigns", "One scheduled, one pending approval, one draft"],
  [
    "campaign_activity",
    "Fill campaign metrics",
    "Simulates members joining campaigns and earning campaign bonuses",
  ],
  ["segments", "Custom segment", "\"Big spenders\" next to the lifecycle segments"],
  ["member_mix", "Tier spread and blocked members", "Moves about a quarter of new members up a tier and blocks a few"],
  ["refresh_insights", "Recalculate insights", "So the dashboard and insights show the new data right away"],
] as const;

const PHASE_LABEL: Record<TestDataRun["phase"], string> = {
  setup: "Setting up outlets, rules, fraud, campaigns and segments…",
  members: "Enrolling members…",
  transactions: "Posting purchases and redemptions…",
  finishing: "Dating history and recalculating insights…",
  done: "Finished",
};

function TestDataTab({ programId, programStatus }: { programId: string; programStatus?: string }) {
  const queryClient = useQueryClient();
  const [options, setOptions] = useState<TestDataOptions>({
    members: 25,
    transactions_per_member: 4,
    redeem_pct: 25,
    min_amount: 50,
    max_amount: 1500,
    history_days: 60,
    outlets: true,
    rules: true,
    fraud: true,
    campaigns: true,
    campaign_activity: true,
    segments: true,
    member_mix: true,
    refresh_insights: true,
  });
  const set = <K extends keyof TestDataOptions>(key: K, value: TestDataOptions[K]) =>
    setOptions((prev) => ({ ...prev, [key]: value }));

  const statusQuery = useQuery({
    queryKey: testDataKeys.status(programId),
    queryFn: () => TestDataApi.status(programId),
  });
  const maxHistory = statusQuery.data?.max_history_days ?? 89;
  const [runId, setRunId] = useState<string | null>(null);
  // Pick up a run that is still going (e.g. the modal was closed and reopened).
  useEffect(() => {
    if (!runId && statusQuery.data?.latest?.status === "running") setRunId(statusQuery.data.latest.id);
  }, [runId, statusQuery.data]);

  const runQuery = useQuery({
    queryKey: testDataKeys.run(programId, runId ?? ""),
    queryFn: () => TestDataApi.run(programId, runId!),
    enabled: Boolean(runId),
    refetchInterval: (query) => (query.state.data?.status === "running" || !query.state.data ? 1000 : false),
  });
  const run = runQuery.data;
  const running = run?.status === "running";

  // When a run finishes, refresh every screen and say what happened.
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!run || run.status === "running" || announced.current === run.id) return;
    announced.current = run.id;
    queryClient.invalidateQueries({ queryKey: ["perkpoint"] });
    const summary = `${run.setup_created ? `${run.setup_created} setup items, ` : ""}${run.members_created} members and ${run.transactions_posted} transactions`;
    if (run.status === "done") toast.success(`Created ${summary}${run.failed ? ` (${run.failed} failed)` : ""}`);
    else if (run.status === "cancelled") toast.message(`Stopped after ${summary}`);
    else toast.error(run.error || "Test data generation failed");
  }, [run, queryClient]);

  const start = useMutation({
    mutationFn: () => TestDataApi.start(programId, { ...options, history_days: Math.min(options.history_days, maxHistory) }),
    onSuccess: (started) => {
      announced.current = null;
      setRunId(started.id);
      queryClient.setQueryData(testDataKeys.run(programId, started.id), started);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not start test data")),
  });
  const cancel = useMutation({
    mutationFn: () => TestDataApi.cancel(programId, runId!),
    onSuccess: (updated) => queryClient.setQueryData(testDataKeys.run(programId, updated.id), updated),
  });

  const inactive = programStatus !== undefined && programStatus !== "Active";
  const percent = run && run.total ? Math.round((run.done / run.total) * 100) : 0;

  if (statusQuery.data && !statusQuery.data.enabled) {
    return <Notice tone="warn">Test data is turned off on this server (production).</Notice>;
  }

  return (
    <div className="space-y-5">
      <Notice tone="info">
        The server fills this program so no screen is empty: rules, fraud alerts, campaigns, segments,
        and members with a history of purchases spread over the days you pick. Setup items are only
        added when the program has none, so running it again just adds members and history. Members
        get <span className="font-mono">@example.test</span> emails and transactions get
        <span className="font-mono"> TEST-</span> references.
      </Notice>
      {inactive ? (
        <Notice tone="warn">
          This program is {programStatus?.toLowerCase()}. Members can be added, but transactions only
          post while the program is Active.
        </Notice>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField id="td-members" label="Members to create" value={options.members}
          onChange={(v) => set("members", v)} min={1} max={500} />
        <NumberField id="td-txns" label="Transactions per member" value={options.transactions_per_member}
          onChange={(v) => set("transactions_per_member", v)} min={0} max={20} />
        <NumberField id="td-min" label="Lowest invoice amount" value={options.min_amount}
          onChange={(v) => set("min_amount", v)} min={1} max={1_000_000} />
        <NumberField id="td-max" label="Highest invoice amount" value={options.max_amount}
          onChange={(v) => set("max_amount", v)} min={1} max={1_000_000} hint="In program currency" />
        <NumberField id="td-history" label="Spread history over (days)" value={Math.min(options.history_days, maxHistory)}
          onChange={(v) => set("history_days", v)} min={0} max={maxHistory}
          hint={`Enrolments and purchases are dated across this window. Up to ${maxHistory} days for this program.`} />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <Label className="font-medium text-slate-700">Redemptions</Label>
          <span className="tabular-nums text-slate-900">{options.redeem_pct}% of transactions</span>
        </div>
        <Slider value={[options.redeem_pct]} min={0} max={60} step={5}
          onValueChange={([v]) => set("redeem_pct", v)} aria-label="Share of transactions that are redemptions" />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-slate-900">Also set up</p>
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {SETUP_ITEMS.map(([key, title, hint]) => (
            <label key={key} className="flex items-start justify-between gap-4 px-3 py-2.5">
              <span>
                <span className="block text-sm font-medium text-slate-900">{title}</span>
                <span className="block text-xs text-muted-foreground">{hint}</span>
              </span>
              <Switch checked={options[key]} onCheckedChange={(v) => set(key, v)} aria-label={title} />
            </label>
          ))}
        </div>
      </div>

      {run ? (
        <div className="space-y-2 rounded-xl border border-slate-200 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-medium text-slate-800">
              {run.status === "running"
                ? PHASE_LABEL[run.phase]
                : run.status === "cancelled"
                  ? "Stopped"
                  : run.status === "failed"
                    ? "Failed"
                    : "Finished"}
            </span>
            <span className="tabular-nums text-slate-600">
              {run.setup_created ? `${run.setup_created} setup items · ` : ""}
              {run.members_created} members · {run.issuance_posted ?? 0} issuance · {run.redemptions_posted ?? 0} redemptions
              {run.failed ? ` · ${run.failed} failed` : ""}
            </span>
          </div>
          <Progress value={percent} className="h-2" />
          {run.log.length ? (
            <div className="max-h-32 overflow-y-auto rounded-md bg-slate-50 px-2 py-1.5 font-mono text-[11px] leading-relaxed text-slate-600">
              {run.log.map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
        <p className="text-xs text-muted-foreground">
          Runs on the server, so you can close this window; reopen it to see progress.
        </p>
        <div className="flex gap-2">
          {running ? (
            <Button type="button" variant="outline" className="bg-white"
              disabled={cancel.isPending || run?.cancel_requested} onClick={() => cancel.mutate()}>
              {run?.cancel_requested ? "Stopping…" : "Stop"}
            </Button>
          ) : null}
          <Button type="button" className="bg-green-600 text-white hover:bg-green-700"
            disabled={running || start.isPending} onClick={() => {
              if (options.max_amount < options.min_amount) {
                toast.error("The highest invoice must be at least the lowest.");
                return;
              }
              start.mutate();
            }}>
            {running || start.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Database className="mr-1.5 h-4 w-4" />}
            Generate test data
          </Button>
        </div>
      </div>
    </div>
  );
}

function InsightsTab({ programId }: { programId: string }) {
  const queryClient = useQueryClient();
  const runsQuery = useQuery({
    queryKey: [...insightKeys.all, programId, "runs"],
    queryFn: () => InsightsApi.runs(programId, 5),
  });
  const refresh = useMutation({
    mutationFn: () => InsightsApi.refresh(programId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: insightKeys.all });
      toast.success(`Insights recalculated for ${result.members_scored} members`);
    },
    onError: (error) => toast.error(apiErrorMessage(error, "Could not recalculate insights")),
  });

  return (
    <div className="space-y-5">
      <Notice tone="info">
        The dashboard, member insights and segments read saved results. They recalculate every night
        at 2am program time, and right away when segment rules change. Recalculate now after
        generating test data to see it on those screens.
      </Notice>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Recalculate insights now</p>
          <p className="text-xs text-muted-foreground">
            Re-scores every member, re-sorts segments and rebuilds the 7, 30 and 90 day views.
          </p>
        </div>
        <Button
          type="button"
          className="bg-green-600 text-white hover:bg-green-700"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`} />
          Recalculate
        </Button>
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold text-slate-900">Recent runs</p>
        {runsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (runsQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No runs yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {(runsQuery.data ?? []).map((run) => (
              <li key={run.id} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span className="text-slate-800">
                  {new Date(run.started_at).toLocaleString("en-GB", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {run.trigger === "rule_change" ? "Rule change" : run.trigger === "manual" ? "Manual" : "Scheduled"}
                  </span>
                </span>
                <span className={run.status === "done" ? "text-slate-600" : "text-red-600"}>
                  {run.status === "done" ? `${run.members_scored} members scored` : run.error || run.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Perk Point settings: test data and insights tools for the active program. */
export function PerkPointSettingsModal() {
  const [open, setOpen] = useState(false);
  const { programId } = useActiveProgramId();
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: open && Boolean(programId),
  });

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-4xl">
        {!programId ? (
          <div className="space-y-4 p-6">
            <DialogHeader>
              <DialogTitle>Settings</DialogTitle>
              <DialogDescription>Program tools</DialogDescription>
            </DialogHeader>
            <Notice tone="warn">Log in as manager of a program to use these tools.</Notice>
          </div>
        ) : (
          <Tabs
            defaultValue="test-data"
            orientation="vertical"
            className="flex h-[min(640px,85vh)] flex-col sm:flex-row"
          >
            {/* Left rail */}
            <div className="flex shrink-0 flex-col border-b border-slate-200 bg-slate-50/80 p-3 sm:w-56 sm:border-b-0 sm:border-r">
              <DialogHeader className="px-2 pb-3 pt-1 text-left">
                <DialogTitle className="text-base">Settings</DialogTitle>
                <DialogDescription className="truncate text-xs">
                  {programQuery.data
                    ? `${programQuery.data.name} · ${programQuery.data.programCode}`
                    : "Program tools"}
                </DialogDescription>
              </DialogHeader>
              <TabsList className="flex h-auto flex-row justify-start gap-1 bg-transparent p-0 sm:flex-col sm:items-stretch">
                {SECTIONS.map((section) => (
                  <TabsTrigger
                    key={section.id}
                    value={section.id}
                    className="justify-start gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-slate-200"
                  >
                    <section.icon className="h-4 w-4" />
                    {section.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1 overflow-y-auto">
              {SECTIONS.map((section) => (
                <TabsContent key={section.id} value={section.id} className="mt-0 p-6">
                  <div className="mb-5 border-b border-slate-100 pb-4 pr-8">
                    <h2 className="text-lg font-semibold text-slate-900">{section.label}</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">{section.hint}</p>
                  </div>
                  {section.id === "test-data" ? (
                    <TestDataTab programId={programId} programStatus={programQuery.data?.status} />
                  ) : (
                    <InsightsTab programId={programId} />
                  )}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SECTIONS = [
  {
    id: "test-data",
    label: "Test data",
    hint: "Fill this program with demo members and transactions.",
    icon: Database,
  },
  {
    id: "insights",
    label: "Insights",
    hint: "Recalculate the dashboard, insights and segments.",
    icon: BarChart3,
  },
] as const;
